import { Logger } from '@/Logger';

import type { APITeoMarker, ExcerptContext, MarkerGroupName } from '../TeoApi';
import { arraysEqual } from '../utility/miscFunctions';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { TeoText } from './TeoText';

const LOG = Logger('MarkerManager');

export type MarkerContent = { markerId: string; text: string };

export type MarkerManagerEvents = {
  markerClicked: (detail: {
    markerId: string;
    // context: ExcerptContext;
    marker: APITeoMarker;
  }) => void;
  markersFocused: (detail: { markerIds: string[] }) => void;
  markerReplacement: (detail: { markerId: string; newContent: string }) => void;
  markerClickTriggered: (detail: { markerId: string }) => void;
  markersAttached: (detail: { markerGroup: MarkerGroupName }) => void;
};

export class MarkerManager {
  public events = new TeoEventTarget<MarkerManagerEvents>();
  clickableMarkers: string[] = [];
  focusedMarkers: string[] = [];
  private markerContentsReader: ((markerIds: string[]) => MarkerContent[]) | null = null;
  private markerContextReader:
    | ((focusMarkerId: string, parentType?: string) => ExcerptContext | undefined)
    | null = null;

  constructor(private text: TeoText) {
    this.text.store.addEventListener('view', () => {
      this.setMarkers([], []);
    });
  }

  setMarkers(markers: string[], clickableMarkers: string[] = []) {
    const cMarkers = Array.from(new Set(markers));
    const cClickable = Array.from(new Set(clickableMarkers));
    const clickableMarkersChanged = !arraysEqual(this.clickableMarkers, cClickable);
    this.clickableMarkers = cClickable;
    markers = Array.from(new Set([...cMarkers, ...cClickable]));
    this.text.ui.update('markers', markers, clickableMarkersChanged);
  }

  addMarkers(markers: string[], clickableMarkers: string[] = [], resetClickable = false) {
    const updatedMarkers = [...this.text.ui.state.markers, ...markers];
    const clickable =
      resetClickable ? clickableMarkers : [...this.clickableMarkers, ...clickableMarkers];
    this.setMarkers(updatedMarkers, clickable);
  }

  removeMarkers(markers: string[]) {
    const clickableMarkers = this.clickableMarkers.filter((marker) => !markers.includes(marker));
    const updatedMarkers = this.text.ui.state.markers.filter(
      (marker) => !markers.includes(marker) && !clickableMarkers.includes(marker),
    );
    this.setMarkers(updatedMarkers, clickableMarkers);
  }

  checkMarkerVisibility(markerName: string): boolean {
    if (!this.text.ui.state.markers) return false;
    return this.text.ui.state.markers.includes(markerName);
  }

  syncPerformanceMarkers() {
    LOG.dev('syncPerformanceMarkers >>>', this.text.analysis, this.text.ui.state);
    if (
      this.text.analysis === null ||
      !this.text.ui.state.dimension ||
      !this.text.ui.state.textProperty
    ) {
      this.setMarkers([]);
      return;
    }
    const markers = this.text.analysis.getCurrentTextPropertyMarkerNames();
    this.setMarkers(markers ?? []);
  }

  triggerMarkerClick(markerId: string) {
    this.events.triggerEvent('markerClickTriggered', { markerId });
  }

  clickMarker(markerId: string, marker: APITeoMarker) {
    this.events.triggerEvent('markerClicked', {
      markerId,
      marker,
    });
  }

  replaceMarkerContent(markerId: string, newContent: string) {
    this.events.triggerEvent('markerReplacement', {
      markerId,
      newContent,
    });
  }

  /**
   * For the editor to report that it has put the markers of a group on its text, which is
   * when their content can be read and replaced.
   */
  reportMarkersAttached(markerGroup: MarkerGroupName) {
    this.events.triggerEvent('markersAttached', { markerGroup });
  }

  /**
   * Registers the editor function that reads the current content of markers.
   */
  updateMarkerContentsFunction(reader: (markerIds: string[]) => MarkerContent[]) {
    this.markerContentsReader = reader;
  }

  /**
   * Registers the editor function that builds a live excerpt around a marker.
   */
  updateMarkerContextFunction(
    reader: (focusMarkerId: string, parentType?: string) => ExcerptContext | undefined,
  ) {
    this.markerContextReader = reader;
  }

  get canReadMarkerContents(): boolean {
    return this.markerContentsReader !== null;
  }

  /**
   * Current content of the given markers, in document order.
   * @param markerIds Markers to read. Markers without content in the editor are omitted.
   */
  getMarkerContents(markerIds: string[]): MarkerContent[] {
    if (!this.markerContentsReader) {
      LOG.error('MarkerManager: no editor is registered to read marker contents from.');
      return [];
    }
    return this.markerContentsReader(markerIds);
  }

  /**
   * Live excerpt around a marker, read from the editor.
   * When `parentType` is set (e.g. `'sentence'`), the excerpt wraps the overlapping parent
   * marker attached to the same nodes. Falls back to the focused marker if none is found.
   * @returns `undefined` when no editor is registered or the marker is not attached.
   */
  createMarkerContext(focusMarkerId: string, parentType?: string): ExcerptContext | undefined {
    if (!this.markerContextReader) {
      LOG.error('MarkerManager: no editor is registered to read marker context from.');
      return;
    }
    return this.markerContextReader(focusMarkerId, parentType);
  }

  focusMarkers(markerIds: string[]) {
    this.focusedMarkers = markerIds;
    this.events.triggerEvent('markersFocused', {
      markerIds,
    });
  }

  addFocusedMarker(markerId: string) {
    if (!this.focusedMarkers.includes(markerId)) {
      this.focusedMarkers.push(markerId);
      this.events.triggerEvent('markersFocused', {
        markerIds: this.focusedMarkers,
      });
    }
  }

  removeFocusedMarker(markerId: string) {
    const index = this.focusedMarkers.indexOf(markerId);
    if (index !== -1) {
      this.focusedMarkers.splice(index, 1);
      this.events.triggerEvent('markersFocused', {
        markerIds: this.focusedMarkers,
      });
    }
  }

  clearFocusedMarkers() {
    this.focusedMarkers = [];
    this.events.triggerEvent('markersFocused', {
      markerIds: [],
    });
  }
}
